/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiFormRow,
  EuiFieldText,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { TrafficFilterRule } from '@modules/cloud-api/v1/types'

type Props = {
  rules: TrafficFilterRule[]
  onChange: (rules: TrafficFilterRule[]) => void
}

const SourceField: React.FunctionComponent<Props> = ({ rules, onChange }) => {
  // initialize with at least one empty rule
  const rulesList = rules.length > 0 ? rules : [{ source: '' }]

  const onChangeSource = (index: number, value: string) => {
    const updatedRules = [...rulesList]

    if (!updatedRules[index]) {
      updatedRules[index] = {}
    }

    updatedRules[index].source = value
    onChange(updatedRules)
  }

  const onAddSource = () => {
    const updatedRules = [...rulesList, { source: '' }]
    onChange(updatedRules)
  }

  const onRemoveSource = (index: number) => {
    const updatedRules = [...rulesList]
    updatedRules.splice(index, 1)

    // ensure there's always at least one rule
    if (updatedRules.length === 0) {
      updatedRules.push({ source: '' })
    }

    onChange(updatedRules)
  }

  return (
    <EuiFormRow
      helpText={
        <FormattedMessage
          id='sourceField.placeholder'
          defaultMessage='Enter CIDR or IP address, e.g. 192.256.123.6/22'
        />
      }
    >
      <React.Fragment>
        {rulesList.map((rule, i) => (
          <React.Fragment key={i}>
            {i > 0 && <EuiSpacer size='s' />}

            <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
              <EuiFlexItem grow={false}>
                <div style={{ width: '400px' }}>
                  <EuiFormRow
                    label={
                      <FormattedMessage
                        id='sourceField.label'
                        defaultMessage='Source {index}'
                        values={{ index: rulesList.length > 1 ? i + 1 : '' }}
                      />
                    }
                  >
                    <EuiFieldText
                      value={rule.source || ''}
                      onChange={(e) => onChangeSource(i, e.target.value)}
                      fullWidth={true}
                    />
                  </EuiFormRow>
                </div>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <div style={{ marginTop: '30px' }}>
                  <EuiFlexGroup gutterSize='s' alignItems='flexStart'>
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon
                        aria-label='Remove source'
                        iconType='minusInCircleFilled'
                        color='primary'
                        onClick={() => onRemoveSource(i)}
                        disabled={rulesList.length === 1}
                      />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      {i === rulesList.length - 1 ? (
                        <EuiButtonIcon
                          aria-label='Add source'
                          iconType='plusInCircleFilled'
                          color='primary'
                          onClick={onAddSource}
                        />
                      ) : (
                        <div style={{ width: '24px' }} />
                      )}
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </div>
              </EuiFlexItem>
            </EuiFlexGroup>
          </React.Fragment>
        ))}
      </React.Fragment>
    </EuiFormRow>
  )
}

export default SourceField
